import { DateTime } from 'luxon';

import { toHeightInches, toWeightLbs } from 'utils/units';

interface CuffRecommendationInput {
  heightRaw: string | undefined;
  heightInches: string | undefined;
  heightUnit: string;
  weightRaw: string | undefined;
  weightUnit: string;
  gender: string | undefined;
  bday: string | null | undefined;
}

const toAgeYears = (bday: string | null | undefined): number | null => {
  if (!bday) return null;
  const birth = DateTime.fromISO(bday);
  if (!birth.isValid) return null;
  return DateTime.utc().diff(birth, 'years').years;
};

export const canRecommendCuffFor = (patientInfo: {
  gender?: string;
  bday?: string | null;
}): boolean =>
  (patientInfo.gender === 'male' || patientInfo.gender === 'female') &&
  !!toAgeYears(patientInfo.bday);

export const getDefaultCuff = (
  options: IDeviceOrderingOption[]
): IDeviceOrderingOption | undefined => {
  const cuffOptions = options.filter((option) => Boolean(option.details));
  if (!cuffOptions.length) return undefined;
  const defaultOption = cuffOptions.find((o) => o.isDefaultAccessory);
  if (defaultOption) return defaultOption;
  return cuffOptions.reduce((best, option) => {
    const { cm } = option.details!;
    const { cm: bestCm } = best.details!;
    return cm.max - cm.min > bestCm.max - bestCm.min ? option : best;
  });
};

// From Confluence:
// Men (AC size)
// AC (cm)=32.52145+0.10975×weight−0.26057×height−0.03028×age
const calcRecommendedCuffSizeForMen = (weight: number, height: number, age: number) => {
  return 32.52145 + 0.10975 * weight - 0.26057 * height - 0.03028 * age;
};

// From Confluence:
// Women (AC size)
// AC (cm)=30.22126+0.13534×weight−0.34121×height+0.09014×age−0.00082565×age2
const calcRecommendedCuffSizeForWomen = (weight: number, height: number, age: number) => {
  return (
    30.22126 + 0.13534 * weight - 0.34121 * height + 0.09014 * age - 0.00082565 * Math.pow(age, 2)
  );
};

export const calcRecommendedArmCircumference = ({
  heightRaw,
  heightInches,
  heightUnit,
  weightRaw,
  weightUnit,
  gender,
  bday
}: CuffRecommendationInput): number | undefined => {
  if (gender !== 'male' && gender !== 'female') return undefined;

  const heightIn = toHeightInches(heightRaw, heightInches, heightUnit);
  const weightLbs = toWeightLbs(weightRaw, weightUnit);
  const ageYears = toAgeYears(bday);

  if (!heightIn || !weightLbs || !ageYears) return undefined;

  return gender === 'male'
    ? calcRecommendedCuffSizeForMen(weightLbs, heightIn, ageYears)
    : calcRecommendedCuffSizeForWomen(weightLbs, heightIn, ageYears);
};

export const selectCuff = (acCm: number, options: IDeviceOrderingOption[]): string | undefined => {
  const cuffOptions = options.filter((option) => Boolean(option.details));
  if (!cuffOptions.length) return undefined;

  const sorted = [...cuffOptions].sort((a, b) => a.details!.cm.min - b.details!.cm.min);
  for (let i = 0; i < sorted.length - 1; i++) {
    const midpoint = (sorted[i].details!.cm.max + sorted[i + 1].details!.cm.min) / 2;
    if (acCm <= midpoint) return sorted[i].sku;
  }
  return sorted[sorted.length - 1].sku;
};
